import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, List, ListItem, ListItemText, IconButton, Chip, CircularProgress, Alert, Toolbar, TextField, InputAdornment, Pagination, PaginationItem, FormControl, Select, MenuItem, Typography, Button, ListItemIcon
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon, AccountCircle as UserIcon, PermIdentity as IdIcon } from '@mui/icons-material';
import Swal from 'sweetalert2';
import api from '../services/api';
import UserModal from './UserModal';

const Users = () => {
  const [data, setData] = useState({
    users: [],
    total: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [page, setPage] = useState(1); // Ajustado para 1-indexed
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get(`/users?page=${page}&limit=${rowsPerPage}`);
      setData({
        users: response.data.users,
        total: response.data.total
      });
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch users', error);
      setError('Failed to fetch users');
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleEditUser = (user) => {
    setCurrentUser(user);
    setModalOpen(true);
  };

  const handleDeleteUser = (user) => {
    Swal.fire({
      title: 'Você tem certeza?',
      text: `Deseja realmente excluir o usuário ${user.username}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir!',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/users/delete-user/${user.id}`);
          setData((prevData) => ({
            ...prevData,
            users: prevData.users.filter((u) => u.id !== user.id)
          }));
          Swal.fire('Excluído!', 'O usuário foi excluído.', 'success');
          fetchUsers();
        } catch (error) {
          Swal.fire('Erro!', 'Falha ao excluir o usuário.', 'error');
        }
      }
    });
  };

  const handleAddUser = () => {
    setCurrentUser(null);
    setModalOpen(true);
  };

  const handleSaveUser = async (user) => {
    try {
      if (user.id) {
        await api.put(`/users/${user.id}`, user);
        setData((prevData) => ({
          ...prevData,
          users: prevData.users.map((u) => (u.id === user.id ? user : u))
        }));
      } else {
        const response = await api.post('/users', user);
        setData((prevData) => ({
          ...prevData,
          users: [response.data, ...prevData.users].slice(0, rowsPerPage)
        }));
      }
      setModalOpen(false);
      Swal.fire('Sucesso!', 'Usuário salvo com sucesso.', 'success');
      fetchUsers();
    } catch (error) {
      Swal.fire('Erro!', 'Falha ao salvar o usuário.', 'error');
    }
  };

  const getStatusLabel = (status) => {
    return status === 1 ? 'Ativo' : 'Inativo';
  };

  const handleChangePage = (event, value) => {
    setPage(value);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredUsers = data.users.filter(user =>
    user.username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'Roboto' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'Roboto' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <Box className="users-container" sx={{ width: '50%', padding: 3, backgroundColor: 'white', borderRadius: 2, boxShadow: 3, fontFamily: 'Roboto' }}>
        <Typography variant="h4" sx={{ textAlign: 'center', marginBottom: 3 }}>Lista de Usuários</Typography>
        <Toolbar sx={{ height: '40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
          <TextField
            placeholder="Buscar..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ width: '30%', backgroundColor: 'white' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              style: { fontFamily: 'Roboto' },
            }}
            InputLabelProps={{ style: { fontFamily: 'Roboto' } }}
          />
          <FormControl sx={{ minWidth: 60 }}>
            <Select
              labelId="rows-per-page-label"
              id="rows-per-page"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              sx={{ height: '40px', fontFamily: 'Roboto' }}
              InputProps={{ style: { fontFamily: 'Roboto' } }}
            >
              <MenuItem value={5} sx={{ fontFamily: 'Roboto' }}>5</MenuItem>
              <MenuItem value={10} sx={{ fontFamily: 'Roboto' }}>10</MenuItem>
              <MenuItem value={25} sx={{ fontFamily: 'Roboto' }}>25</MenuItem>
            </Select>
          </FormControl>
        </Toolbar>
        <List sx={{ width: '100%', backgroundColor: 'background.paper' }}>
          {filteredUsers.map((user) => (
            <ListItem key={user.id} sx={{ borderBottom: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <ListItemIcon>
                  <IdIcon />
                </ListItemIcon>
                <ListItemText
                  primary={`${user.id}`}
                  sx={{ flex: '1 1 auto', fontFamily: 'Roboto' }}
                />
                <Chip
                  label={getStatusLabel(user.status)}
                  sx={{
                    width: 80,
                    bgcolor: user.status === 1 ? 'green' : 'red',
                    color: 'white',
                    fontFamily: 'Roboto',
                    marginRight: 2
                  }}
                />
                <IconButton color="primary" aria-label="edit user" onClick={() => handleEditUser(user)}>
                  <EditIcon />
                </IconButton>
                <IconButton color="secondary" aria-label="delete user" onClick={() => handleDeleteUser(user)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <ListItemIcon>
                  <UserIcon />
                </ListItemIcon>
                <ListItemText
                  primary={`${user.username}`}
                  sx={{ flex: '1 1 auto', fontFamily: 'Roboto' }}
                />
              </Box>
            </ListItem>
          ))}
        </List>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="contained" color="primary" onClick={handleAddUser} startIcon={<AddIcon />} sx={{ marginBottom: 2 }}>
            Adicionar Usuário
          </Button>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Pagination
            count={Math.ceil(data.total / rowsPerPage)}
            page={page}
            onChange={handleChangePage}
            renderItem={(item) => (
              <PaginationItem
                {...item}
                sx={{ '&.Mui-selected': { backgroundColor: 'primary.main', color: 'white', fontFamily: 'Roboto' } }}
              />
            )}
          />
        </Box>
        <UserModal open={modalOpen} onClose={() => setModalOpen(false)} onSave={handleSaveUser} user={currentUser} fetchUsers={fetchUsers} />
      </Box>
    </Box>
  );
};

export default Users;
